/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        // Golf 8 BUG-07 — null-guard. If sticky-atc is placed outside the
        // main-product section (theme-editor preview, advertorial template,
        // custom-liquid block), `.closest()` returns null and every
        // `this.section.querySelector(...)` below would throw. Early-return
        // silently — the element renders as empty without scroll-handlers.
        if (!this.section) return;
        this.mainProductForm = this.section.querySelector('.js-product');
        // Vemorina patch: stock Symmetry looks for <buy-buttons>, but our
        // custom PDP uses <section class="vm-purchase-card"> for the ATC area.
        // Fall back to that selector so the scroll-trigger works on PDPs that
        // use either layout.
        this.buyButtons = this.section.querySelector('buy-buttons, .vm-purchase-card');
        // Golf 8 BUG-07 — second null-guard. No buyButtons = no scroll-target
        // for handleScroll's getBoundingClientRect — must skip registration.
        if (!this.buyButtons) return;
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.updateImage();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Handles submission of the product form.
       *
       * Vemorina patch: stock Symmetry tries to read the variant-id from
       * <buy-buttons> and validate the main <product-form>. Vemorina uses
       * a custom <section class="vm-purchase-card"> for the ATC area, so
       * both selectors fail. We sync the variant-id from the active set-card
       * (1/2/3-pack) in vm-purchase-card, then defer to product-form's
       * native handleSubmit (which submits to Shopify's cart-add endpoint).
       *
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        // Sync variant-id AND quantity from the main vm-purchase-card form
        // into the sticky form. The vm-purchase-card has its own JS that
        // updates [data-vm-variant-input] (size pill) and [data-vm-qty-input]
        // (1/2/3-pack bundle) — we pull the current values from there.
        const mainVariantInput = this.section.querySelector('.vm-purchase-card [data-vm-variant-input]');
        const mainQtyInput = this.section.querySelector('.vm-purchase-card [data-vm-qty-input]');
        const stickyVariantInput = this.querySelector('input[name="id"]');
        const stickyQtyInput = this.querySelector('input[name="quantity"]');

        if (mainVariantInput && stickyVariantInput) {
          stickyVariantInput.value = mainVariantInput.value;
        }
        if (mainQtyInput && stickyQtyInput) {
          stickyQtyInput.value = mainQtyInput.value;
        }

        // Native product-form submit (Shopify AJAX cart-add).
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        // Golf 8 BUG-07 — guard-rail. The constructor's null-guards already
        // skip listener registration if buyButtons is null, so this should
        // never fire in that case — but a defensive check here means a
        // hand-fired call (debug console, test harness) can't crash.
        if (!this.buyButtons) return;
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        const variantAvailable = variant && variant.available;
        const unavailableStr = variant
          ? theme.strings.noStock : this.submitBtn.dataset.unavailableText;

        this.submitBtn.textContent = variantAvailable
          ? this.submitBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
